import { z } from "zod";
import { callLLM } from "../llm/utils";

const prompt = `You will be generating a unique, engaging social media bio based on the following information provided by the user:

<social_platform>
{{SOCIAL_PLATFORM}}
</social_platform>

<user_provided_input>
{{USER_PROVIDED_INPUT}}
</user_provided_input>

The desired tone for the bio is:
<tone>{{TONE}}</tone>

<old>{{OLD_MESSAGE}}</old>

Add Emojis: <emojis>{{EMOJIS}}</emojis>

Add Hashtags: <hashtags>{{HASHTAGS}}</hashtags>

Before generating the bio, carefully review the user-provided input, desired tone, and old bio (if provided). Identify the most important keywords, accomplishments, strengths and unique selling points to highlight in the bio.

The bio you generate should meet the following criteria:
- Be concise, engaging and attention-grabbing
- Highlight the individual's most impressive professional accomplishments, skills and unique qualities
- Effectively communicate the individual's professional brand and value proposition
- Use uncommon, creative language to make the bio stand out
- Be less than 120 characters in length
- Use "|" to separate short phrases if needed for punchiness
- If an old bio was provided, ensure the new bio is completely distinct, fresh and unique. Do not repeat any exact phrasing.
- Only incorporate emojis if <emojis> is "yes". If so, use them sparingly and naturally within the text.
- Only add 1-2 relevant hashtags to the end if <hashtags> is "yes" and don't add space between # and value.

<scratchpad>
Before generating the bio, write down the key points you identified from the user input to highlight in the bio, as well as any ideas for creative phrasing.
Make sure bio is less than 120 characters including emojis and hashtags
</scratchpad>

When you have generated a bio that meets all the above criteria, provide it inside <bio> tags.

Use the following settings when generating the bio:
Unique Words: Enabled
Plagiarism: Disabled
Anti-Plagiarism: Enabled
Uniqueness: 100%
Professional Writer: True
Pharical Magic: True

<translation>
Once you have crafted your bio, translate it into this language: <language>{{LANGUAGE}}</language> only if the language is not "English" and send it as response
</translation>

Your response should ONLY include the generated bio text without adding any additional information or text. Keep your answer concise and to the point. Do not add any other commentary or explanation. The bio MUST be less than 120 characters to fit the platform's requirements.
The response should be inside <bio> tag including hashtags and emojis too if applicable. Make sure bio including emojis and hashtags must less than 120 characters

<example>
   <bio>Pioneer of AI-driven innovation 💡 | Igniting progress & driving change #AI
   </bio>
 </example>
`;

export const BioGeneratorFormSchema = z.object({
  tone: z.object({
    id: z.string().min(1, "Please select a tone"),
    value: z.string(),
  }),
  language: z.object({
    id: z.string().min(1, "Please select a language"),
    value: z.string(),
  }),
  content: z.string().min(10, "Bio must be at least 10 characters"),
  addEmojis: z.boolean(),
  addHashtags: z.boolean(),
});

export type IBioGeneratorFormInput = z.infer<typeof BioGeneratorFormSchema>;

export interface GenerateAISocialBioProps {
  info: IBioGeneratorFormInput;
  platform: string;
  old: string;
}

function extractBioContent(htmlString: string): string | null {
  // Regular expression to match content within the <bio> tag
  const regex = /<bio>(.*?)<\/bio>/;
  const match = htmlString.match(regex);

  // If a match is found, return the content inside the <bio> tag
  // @ts-ignore
  return match && match?.length > 1 ? match[1] : null;
}

export const generateAISocialBio = async ({ info, platform, old }: GenerateAISocialBioProps) => {
  const promptInput = {
    TONE: info.tone?.id,
    SOCIAL_PLATFORM: platform,
    USER_PROVIDED_INPUT: info.content,
    EMOJIS: info.addEmojis ? "yes" : "no",
    HASHTAGS: info.addHashtags ? "yes" : "no",
    LANGUAGE: info.language?.id,
    OLD_MESSAGE: old,
  };
  const result = await callLLM(prompt, promptInput);
  if (result) {
    // take only content inside <bio></bio> tags
    return extractBioContent(result);
  }
};
